import React, { MouseEvent, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import useFormatLocaleDate from '@hooks/orders-sales/use-local-language';
import useWindowSize from '@hooks/use-window-size';
import clsx from 'clsx';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import useOrdersQuery from '@hooks/orders-sales/use-orders-query';
import { Order } from '@interfaces/models/myOrder';
import { Product } from '@interfaces/models/orderSaleProduct';
import { State } from '../type';
import ErrorView from '../common-views/error';
import LoadingView from '../common-views/loading';
import NoItemView from '../common-views/empty';
import LoadMoreView from '../common-views/load-more';
import ProductsView from '../product/product';
import styles from '../products.module.scss';
import { getOrderSaleURLQueries } from '../helper';
import LinkButton from '../product/link-button';

const OrdersView: React.FC<{
  state: State;
  updateState: (s: State) => void;
}> = ({ state, updateState }) => {
  const { isWiderThanMd } = useWindowSize();

  const { formatLocaleDate } = useFormatLocaleDate();
  const { sendAnalyticEvent } = useAnalyticEvents('orders_sales_timeline');
  const { t } = useTranslation('orders-sales');
  const { currentTab } = state;

  const { productId: bookmarkProductId, orderId: bookmarkOrderId } = getOrderSaleURLQueries();

  const { isErrorOrder, isLoadingOrder, isLoadingMoreOrder, isEmptyOrder, orderData, fetchOrder } = useOrdersQuery(
    bookmarkOrderId,
    bookmarkProductId,
  );

  const { orders, hasMore } = orderData;

  useEffect(() => {
    if (!isLoadingOrder && orders && orders.length && orders[0].products?.length) {
      const getOrders = (order: Order, product: Product) => {
        updateState({
          ...state,
          orderProduct: isWiderThanMd ? [order, product] : null,
          loading: false,
        });
      };
      getOrders(orders[0], orders[0].products[0]);
    }
  }, [isLoadingOrder]);

  if (isErrorOrder) {
    return (
      <ErrorView
        currentTab={currentTab}
        forTab={'Order'}
      />
    );
  } else if (isLoadingOrder) {
    return (
      <LoadingView
        currentTab={currentTab}
        forTab={'Order'}
      />
    );
  } else if (isEmptyOrder) {
    return (
      <NoItemView
        currentTab={currentTab}
        forTab={'Order'}
      />
    );
  }

  const onLoadMore = () => {
    fetchOrder();
    sendAnalyticEvent('timeline_load_more', { label: 'orders' });
  };

  const onInvoiceClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
    sendAnalyticEvent('timeline_invoice', { label: 'buyer' });
  };

  const negLeft = currentTab !== 'Order' ? 'ordersSales__mainLeft__hasItems--neg' : '';

  return (
    <div className={clsx(styles.ordersSales__mainLeft__hasItems, styles[negLeft])}>
      <div className={styles.ordersSales__mainLeft__orders}>
        {orders.map((order) => {
          // This should be a database/API issue!
          if (order.products == null) {
            return <></>;
          }

          return (
            <div
              data-cy={order.orderId}
              key={order.orderId}
              className={styles.ordersSales__mainLeft__orders__order}
            >
              <div className={styles.ordersSales__mainLeft__orders__order__info}>
                <div className={styles.ordersSales__mainLeft__orders__order__info__text}>
                  {`${t('MY_ACCOUNT.ORDERS_SALES.LIST.ORDER')} ${order.orderId}`}
                  <div className={styles.ordersSales__mainLeft__orders__order__info__text__grey}>
                    {formatLocaleDate(order.orderConfirmationDate)}
                  </div>
                </div>
                <div className={styles.ordersSales__mainLeft__orders__order__prices}>
                  <div className={styles.ordersSales__mainLeft__orders__order__info__price}>
                    {order.orderTotalPrice}
                  </div>
                  {!!order.paymentMethod && (
                    <div className={styles.ordersSales__mainLeft__orders__order__info__price}>
                      {order.paymentMethod}
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.ordersSales__mainLeft__products}>
                <ProductsView
                  state={state}
                  updateState={updateState}
                  order={order}
                  products={order.products}
                />
              </div>

              {!!order.invoiceUrl && (
                <div className={styles.ordersSales__mainLeft__buttonContainer}>
                  <LinkButton
                    name={t('MY_ACCOUNT.ORDERS_SALES.LIST.ORDER_SUMMARY')}
                    href={order.invoiceUrl}
                    onClick={onInvoiceClick}
                  />
                </div>
              )}
            </div>
          );
        })}
        {hasMore && (
          <LoadMoreView
            onClick={onLoadMore}
            loading={isLoadingMoreOrder}
          />
        )}
      </div>
    </div>
  );
};

export default OrdersView;
